import React, { useEffect, useState } from "react";
import { BaseWizardProps } from "../wizard/wizard-step-base";
import { AppDispatch, RootState } from "../../state/store";
import { useDispatch, useSelector } from "react-redux";
import {
  defaultValidate,
  WizardButtonGroup,
} from "../wizard/wizard-button-group-component";
import {
  GoAFormItem,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from "@abgov/react-components";
import { KeyValuePair } from "../../models/key-value-pair";
import { updateCollision } from "../../state/slices/collisionSlice";
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from "../../state/slices/notificationSlice";
import { Collision } from "../../models/collision";
import { NotificationType } from "../notifications/notification-component";

const directionsMapping = [
  {
    label: "North",
    eCollisionLabel: "North",
  },

  {
    label: "South",
    eCollisionLabel: "South",
  },

  {
    label: "East",
    eCollisionLabel: "East",
  },

  {
    label: "West",
    eCollisionLabel: "West",
  },

  {
    label: "Unknown",
    eCollisionLabel: "Unknown",
  },
];

interface DirectionComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected
) => TSelected = useSelector;

export const DirectionComponent = ({
  navigationConfiguration,
}: DirectionComponentProps) => {
  const dispatch = useAppDispatch();

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration
  );

  const [nextPage, setNextPage] = useState("");

  const defaultSelection = "0";

  const headerErrorText = "Make a selection below";
  const errorText = "Choose a direction of travel";
  const [formError, setFormError] = useState("");
  const [selection, setSelection] = useState(
    stateCollision.collision.directionOfTravel
      ? stateCollision.collision.directionOfTravel.toString()
      : defaultSelection
  );

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined
    );

    setNextPage(pageConfig?.default ?? "error");
  }, []);

  const validate = (): boolean => {
    let valid = true;

    if (!selection || selection === defaultSelection) {
      setFormError(errorText);
      valid = false;
    }

    if (!valid) {
      dispatch(
        pushNotification({
          body: headerErrorText,
          notificationType: NotificationType.importantValidationError,
        })
      );
    }

    return valid;
  };

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    updatedCollision.directionOfTravel = parseInt(selection);

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <h2>Direction of travel</h2>
      <GoASpacer vSpacing="l" />
      Select an option below that best matches your direction of travel. eg. for
      northwest, select north. If you're unsure of your direction, select
      'Unknown'
      <GoASpacer vSpacing={"l"} />
      <div>
        <GoAFormItem error={formError}>
          <GoARadioGroup
            name="directionGroup"
            value={selection.toString()}
            onChange={(item, value) => {
              setFormError("");
              setSelection(value);
              dispatch(removeAllValidationErrorNotifications());
            }}
            error={formError.length > 0}
          >
            {directionsMapping.map((direction) => {
              const id = stateConfiguration.configuration.directionOfTravel
                .find(
                  (x) =>
                    x.value.toLowerCase() ===
                    direction.eCollisionLabel.toLowerCase()
                )
                ?.id.toString();

              const control = (
                <GoARadioItem value={id} label={direction.label} key={id} />
              );
              return control;
            })}
          </GoARadioGroup>
        </GoAFormItem>
      </div>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={() => {
          return validate();
        }}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        navigate={navigationConfiguration.navigate}
      />
    </div>
  );
};
